import { useEffect, useRef } from 'react'

import {
  ExamplesBackdrop,
  ExamplesCard,
  ExamplesCaption,
  ExamplesEmpty,
  ExamplesGroup,
  ExamplesGroupTitle,
  ExamplesHandle,
  ExamplesImage,
  ExamplesRow,
  ExamplesSheet,
  ExamplesTitle,
} from './styles'
import {
  TrackerExampleGroup,
  resolveExampleImageUrl,
} from './trackerExamples'

interface ReviewExamplesSheetProps {
  open: boolean
  groups: TrackerExampleGroup[]
  onClose: () => void
}

export const ReviewExamplesSheet = ({
  open,
  groups,
  onClose,
}: ReviewExamplesSheetProps) => {
  const sheetRef = useRef<HTMLDivElement>(null)
  const dragStartY = useRef<number | null>(null)

  useEffect(() => {
    if (!open) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, open])

  if (!open) {
    return null
  }

  const hasImages = groups.some(group => group.images.length > 0)

  return (
    <>
      <ExamplesBackdrop onClick={onClose} aria-hidden />
      <ExamplesSheet
        ref={sheetRef}
        role="dialog"
        aria-label="Tracker examples"
        onPointerDown={event => {
          if ((event.target as HTMLElement).closest('[data-examples-handle]')) {
            dragStartY.current = event.clientY
          }
        }}
        onPointerUp={event => {
          if (dragStartY.current == null) {
            return
          }
          const delta = event.clientY - dragStartY.current
          dragStartY.current = null
          if (delta > 48) {
            onClose()
          }
        }}
        onPointerCancel={() => {
          dragStartY.current = null
        }}
      >
        <ExamplesHandle data-examples-handle type="button" onClick={onClose}>
          <span />
        </ExamplesHandle>
        <ExamplesTitle>Examples</ExamplesTitle>
        {!hasImages ? (
          <ExamplesEmpty>No examples for this tracker.</ExamplesEmpty>
        ) : (
          groups.map(group => (
            <ExamplesGroup key={group.slug}>
              {groups.length > 1 && (
                <ExamplesGroupTitle>{group.label}</ExamplesGroupTitle>
              )}
              <ExamplesRow>
                {group.images.map(image => {
                  const src = resolveExampleImageUrl(image.file)
                  return (
                    <ExamplesCard key={image.file}>
                      {src ? (
                        <ExamplesImage
                          src={src}
                          alt={image.description || group.label}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <ExamplesEmpty>Missing image base URL</ExamplesEmpty>
                      )}
                      {image.description ? (
                        <ExamplesCaption>{image.description}</ExamplesCaption>
                      ) : null}
                    </ExamplesCard>
                  )
                })}
              </ExamplesRow>
            </ExamplesGroup>
          ))
        )}
      </ExamplesSheet>
    </>
  )
}
